/**
 * contractFormat.js — pure presentation helpers for the Wisconsin Contingency
 * Hub: Central-time dates, status labels, attention flags and timeline labels.
 *
 * Every user-facing sentence comes from contractCopy.js (legal's copy file).
 * The API sends instants as naive UTC ISO strings and `deadline_local` as the
 * Central date; parties only ever see Central time (copy §0).
 */

import { contractCopy, hasPlaceholder } from './contractCopy';
import { copyText as paperworkCopyText } from '../../SellerDashboard/paperworkCopy';

export const CENTRAL_TZ = 'America/Chicago';

export const CONTRACT_STATUS = {
  AWAITING_SIGNED_OFFER: 'awaiting_signed_offer',
  OPEN: 'open',
  NO_DEADLINE: 'no_deadline',
  CURE_PENDING: 'cure_pending',
  CURE_TERMS_UNCLEAR: 'cure_terms_unclear',
  LAND_DIVISION_LAPSED: 'land_division_lapsed',
  CURED: 'cured',
  WAIVED: 'waived',
  TREATED_AS_SATISFIED: 'treated_as_satisfied',
  SATISFIED: 'satisfied',
  TERMINATED_BY_NOTICE: 'terminated_by_notice',
  TERMINATED_CURE_LAPSED: 'terminated_cure_lapsed',
  TERMINATED_LAND_DIVISION: 'terminated_land_division',
  // T17 record_ended: an admin recorded, from the parties' documents, that the
  // offer ended. Neutral: it doesn't say how (legal §14).
  TERMINATED_ADMIN_RECORD: 'terminated_admin_record',
  MOOT: 'moot',
  // Copy §15.1: an admin recorded, from a public record or a document both
  // parties signed, that the lead-based paint period doesn't apply (home
  // built in 1978 or later). Terminal, doesn't end the deal, never "satisfied".
  DOES_NOT_APPLY: 'does_not_apply',
};

/** Copy §15.2: the deal-level `termination_reason` for an offer ended by a document both parties signed. */
export const REASON_ADMIN_AGREEMENT = 'admin_record_agreement';

/** Copy §15.1: the admin outcome that sets `does_not_apply`. */
export const OUTCOME_RECORD_NOT_APPLICABLE = 'record_not_applicable';

const DEAL_ENDING = [
  CONTRACT_STATUS.TERMINATED_BY_NOTICE,
  CONTRACT_STATUS.TERMINATED_CURE_LAPSED,
  CONTRACT_STATUS.TERMINATED_LAND_DIVISION,
  CONTRACT_STATUS.TERMINATED_ADMIN_RECORD,
];
const CLEARED = [
  CONTRACT_STATUS.CURED, CONTRACT_STATUS.WAIVED, CONTRACT_STATUS.TREATED_AS_SATISFIED, CONTRACT_STATUS.SATISFIED,
  CONTRACT_STATUS.DOES_NOT_APPLY,
];
const HELD = [CONTRACT_STATUS.NO_DEADLINE, CONTRACT_STATUS.CURE_TERMS_UNCLEAR];

/** Statuses that are final (design §1.2). */
export const TERMINAL_STATUSES = [...CLEARED, ...DEAL_ENDING, CONTRACT_STATUS.MOOT];

/** The status pill's tone: `ended`, `cleared`, `held`, `waiting` or `open`. */
export function statusTone(status) {
  if (DEAL_ENDING.includes(status)) return 'ended';
  if (status === CONTRACT_STATUS.MOOT) return 'moot';
  if (CLEARED.includes(status)) return 'cleared';
  if (HELD.includes(status)) return 'held';
  if (status === CONTRACT_STATUS.CURE_PENDING || status === CONTRACT_STATUS.LAND_DIVISION_LAPSED) return 'waiting';
  return 'open';
}

export const isDealEndingStatus = (status) => DEAL_ENDING.includes(status);

// ─── dates (copy §0: "Friday, October 9, 2026", "…, 4:12 p.m. Central time") ──

/** A Date from the API's naive-UTC ISO string (or one with an offset). */
export function parseApiInstant(value) {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const text = String(value);
  const hasZone = /([zZ]|[+-]\d{2}:?\d{2})$/.test(text);
  const date = new Date(hasZone ? text : `${text}Z`);
  return Number.isNaN(date.getTime()) ? null : date;
}

const LONG_DATE = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };

/** `YYYY-MM-DD` (a calendar date, already Central) -> "Friday, October 9, 2026". */
export function formatLongDate(isoDate) {
  if (!isoDate) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(isoDate));
  if (!match) return null;
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 12));
  return new Intl.DateTimeFormat('en-US', { ...LONG_DATE, timeZone: 'UTC' }).format(date);
}

/** An instant -> its Central calendar date, "Friday, October 9, 2026". */
export function formatCentralDate(value) {
  const date = parseApiInstant(value);
  if (!date) return null;
  return new Intl.DateTimeFormat('en-US', { ...LONG_DATE, timeZone: CENTRAL_TZ }).format(date);
}

/** An instant -> "Friday, October 9, 2026, 4:12 p.m. Central time". */
export function formatCentralInstant(value) {
  const date = parseApiInstant(value);
  if (!date) return null;
  const time = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric', minute: '2-digit', hour12: true, timeZone: CENTRAL_TZ,
  }).formatToParts(date);
  const part = (type) => time.find((p) => p.type === type)?.value || '';
  const meridiem = part('dayPeriod').toLowerCase().startsWith('p') ? 'p.m.' : 'a.m.';
  return `${formatCentralDate(date)}, ${part('hour')}:${part('minute')} ${meridiem} Central time`;
}

/** Today's Central date as `YYYY-MM-DD` (for date inputs). */
export function centralToday(now = new Date()) {
  const parts = new Intl.DateTimeFormat('en-CA', {
    year: 'numeric', month: '2-digit', day: '2-digit', timeZone: CENTRAL_TZ,
  }).formatToParts(now);
  const get = (type) => parts.find((p) => p.type === type)?.value;
  return `${get('year')}-${get('month')}-${get('day')}`;
}

/** Milliseconds until an API instant (negative once passed); null without one. */
export function msUntil(value, now = Date.now()) {
  const date = parseApiInstant(value);
  return date ? date.getTime() - now : null;
}

// ─── contract facts ────────────────────────────────────────────────────────

export const roleLabel = (role) => (role === 'seller' ? 'Seller' : role === 'buyer' ? 'Buyer' : null);

/** The contract's deadline as a long Central date. */
export function deadlineDate(contract) {
  return formatLongDate(contract?.deadline_local) || formatCentralDate(contract?.deadline);
}

/** The cure deadline as a long Central date. */
export function cureDeadlineDate(contract) {
  return formatCentralDate(contract?.cure?.deadline);
}

/** The buyer's not-satisfied notice on this contingency, if delivered. */
export function buyerNotice(contract) {
  return (contract?.notices || []).find((n) => n.notice_type === 'not_satisfied') || null;
}

/** The Central date the buyer's not-satisfied notice was delivered. */
export function buyerNoticeDate(contract) {
  return formatCentralDate(buyerNotice(contract)?.delivered_at);
}

/** The seller's notice declining to cure on this contingency, if delivered. */
export function sellerDeclineNotice(contract) {
  return (contract?.notices || []).find((n) => n.notice_type === 'seller_declines_cure') || null;
}

/** The Central date the seller's decline-to-cure notice was delivered. */
export function declineNoticeDate(contract) {
  return formatCentralDate(sellerDeclineNotice(contract)?.delivered_at);
}

/**
 * Statuses with no `WI.hub.status.*` key of their own, and the reviewed label
 * they use. `terminated_admin_record` uses "Offer recorded as ended" (copy §4
 * `WI.timeline.deal_terminated`), never "Ended the offer", which would say the
 * contingency ended it: the documents an admin recorded show how it ended.
 */
const STATUS_LABEL_KEY = {
  [CONTRACT_STATUS.TERMINATED_ADMIN_RECORD]: 'WI.timeline.deal_terminated',
};

/** The pill text for a contract status (copy §4 `WI.hub.status.*`). */
export function statusLabel(contract, role) {
  const status = contract?.status;
  if (!status) return null;
  const vars = { deadline_date: deadlineDate(contract), cure_deadline_date: cureDeadlineDate(contract) };
  if (status === CONTRACT_STATUS.OPEN) {
    if (!vars.deadline_date) return contractCopy('WI.hub.status.no_deadline');
    return contractCopy(role === 'seller' ? 'WI.hub.status.open.seller' : 'WI.hub.status.open', vars);
  }
  if (status === CONTRACT_STATUS.CURE_PENDING && !vars.cure_deadline_date) {
    return contractCopy('WI.hub.status.cure_terms_unclear');
  }
  // The seller declined: the status and deadline are unchanged, but "waiting
  // for the seller's cure decision" no longer describes it (QA 2026-10-16).
  if (status === CONTRACT_STATUS.CURE_PENDING && sellerDeclineNotice(contract)) {
    return contractCopy(
      role === 'seller' ? 'WI.hub.status.cure_pending.declined.seller' : 'WI.hub.status.cure_pending.declined',
      vars,
    );
  }
  return contractCopy(STATUS_LABEL_KEY[status] || `WI.hub.status.${status}`, vars) || status.replace(/_/g, ' ');
}

/**
 * The display-only note for a passed deadline the job hasn't acted on
 * (copy §4): never "treated as satisfied" until the server persists it.
 */
export function deadlinePassedNote(contract) {
  if (!contract?.deadline_passed || contract.status !== CONTRACT_STATUS.OPEN) return null;
  const vars = { deadline_date: deadlineDate(contract) };
  if (!vars.deadline_date) return null;
  if (contract.deadline_basis === 'lead_paint_default') {
    return contractCopy('WI.hub.lead_paint_default.period_ended', vars);
  }
  return contractCopy('WI.hub.deadline_passed_unrecorded', vars);
}

const CURE_RIGHT_RECORD_KEY = {
  elected: 'WI.termination_record.cure.cure',
  none: 'WI.termination_record.cure.no_cure',
  unknown: 'WI.termination_record.cure.unknown',
  excluded: 'WI.termination_record.cure.excluded',
  not_applicable: 'WI.termination_record.cure.not_applicable',
};

/** "Seller's right to cure as printed in the offer" (copy §7 labels). */
export function cureRightLabel(cure) {
  const key = CURE_RIGHT_RECORD_KEY[cure?.right];
  if (!key) return null;
  const text = contractCopy(key, { cure_days: cure?.days });
  return hasPlaceholder(text) ? null : text;
}

/** "How the deadline was set" (copy §7 `WI.termination_record.basis.*`). */
export function deadlineBasisLabel(basis) {
  return basis ? contractCopy(`WI.termination_record.basis.${basis}`) : null;
}

/** On-time label for a delivered notice (copy §7 `WI.termination_record.on_time.*`). */
export function onTimeLabel(onTime) {
  if (onTime === true) return contractCopy('WI.termination_record.on_time.true');
  if (onTime === false) return contractCopy('WI.termination_record.on_time.false');
  return contractCopy('WI.termination_record.on_time.null');
}

/** The printed title of a notice type (copy §2). */
export const noticeTitle = (noticeType) => contractCopy(`WI.notice.${noticeType}.title`);

// ─── attention flags (copy §9) ─────────────────────────────────────────────

const ATTENTION_MESSAGE_KEY = {
  legacy_anchor: 'WI.transaction.contingency_deadline_legacy',
  cure_terms_unclear: 'WI.transaction.cure_terms_unclear',
  late_notice: 'WI.transaction.late_notice',
  legacy_expired: 'WI.transaction.legacy_expired',
  land_division_disputed: 'WI.transaction.land_division_disputed',
  cure_disputed: 'WI.transaction.cure_disputed',
  attachment_missing: 'WI.transaction.attachment_missing',
  anchor_dates_differ: 'WI.transaction.anchor_dates_differ',
  email_undeliverable: 'WI.transaction.email_undeliverable',
  offplatform_notice: 'WI.transaction.offplatform_notice',
  // Phase 1 reasons and the pre-tracking holds (copy §9; backend copy_keys.ATTENTION_COPY_KEYS).
  legacy_resolved: 'WI.transaction.legacy_resolved',
  not_in_signed_offer: 'WI.transaction.contingency_not_in_signed_offer',
  lead_paint_year_unknown: 'WI.transaction.lead_paint_default_year_unknown',
  deadline_uncomputable: 'WI.transaction.contingency_deadline_uncomputable',
  pre_tracking_deadline_passed: 'WI.transaction.pre_tracking_deadline_passed',
  pre_tracking_legacy_activity: 'WI.transaction.pre_tracking_legacy_activity',
  deadline_changed_by_legacy_extension: 'WI.transaction.deadline_changed_by_legacy_extension',
  // No placeholders on purpose (copy §9): every open row of a deal accepted before tracking shipped.
  pre_tracking_deal: 'WI.transaction.pre_tracking_deal',
  // Copy §15.1: the buyer's "I disagree with this record" on a does_not_apply row. No placeholders.
  not_applicable_disputed: 'WI.transaction.not_applicable_disputed',
};

const FLAG_EVENT_TYPE = {
  land_division_disputed: 'land_division_disputed',
  cure_disputed: 'cure_disputed',
  offplatform_notice: 'offplatform_notice_reported',
  not_applicable_disputed: 'not_applicable_disputed',
};

function latestEvent(events, type) {
  const matching = (events || []).filter((e) => e.event_type === type);
  return matching.length ? matching[matching.length - 1] : null;
}

/**
 * The attention flags on a contingency: `{reason, short, message}` each.
 * `message` is the full copy-§9 paragraph when every placeholder can be
 * filled from what the client knows, else null (the short label stands alone).
 *
 * @param {object} contract - The contingency's `contract` block.
 * @param {object[]} [events] - This contingency's timeline events.
 */
export function attentionFlags(contract, events = []) {
  const reasons = contract?.attention_reasons?.length
    ? contract.attention_reasons
    : (contract?.attention_reason ? [contract.attention_reason] : []);
  const notice = buyerNotice(contract);
  return reasons.map((reason) => {
    const short = contractCopy(`WI.attention.${reason}.short`);
    let message = null;
    if (reason === 'grandfathered_blank') {
      message = paperworkCopyText('WI.transaction.contingency_deadline_missing', { contingencies: contract.heading });
    } else if (ATTENTION_MESSAGE_KEY[reason]) {
      const flagEvent = FLAG_EVENT_TYPE[reason] ? latestEvent(events, FLAG_EVENT_TYPE[reason]) : null;
      message = contractCopy(ATTENTION_MESSAGE_KEY[reason], {
        heading: contract.heading,
        contingencies: contract.heading,
        buyer_notice_date: buyerNoticeDate(contract),
        delivered_at_local: formatCentralInstant(notice?.delivered_at),
        deadline_date: deadlineDate(contract),
        party_role: roleLabel(flagEvent?.actor_type),
        reported_delivered_date: formatLongDate(flagEvent?.details?.reported_delivered_on),
      });
    }
    if (hasPlaceholder(message)) message = null;
    return { reason, short: short || null, message };
  }).filter((flag) => flag.short || flag.message);
}

// ─── deletes refused to keep notice evidence (copy §4) ─────────────────────

/** The 409 `code` a listing or account delete gets while a transaction holds notices. */
export const DELETE_BLOCKED_CODE = 'notice_evidence_retained';

/**
 * Legal's message for a listing or account delete the backend refused with
 * 409 `notice_evidence_retained` (copy §4 `WI.error.delete_blocked_by_notices`),
 * else null so the caller shows its usual error.
 *
 * @param {Error & {status?: number, detail?: any}} err - An APIAgent error.
 * @returns {string|null}
 */
export function deleteBlockedMessage(err) {
  const detail = err && typeof err.detail === 'object' && err.detail !== null ? err.detail : null;
  if (err?.status !== 409 || detail?.code !== DELETE_BLOCKED_CODE) return null;
  return contractCopy('WI.error.delete_blocked_by_notices');
}

// ─── timeline (contingency_events) ─────────────────────────────────────────

const ACTOR_LABEL = { buyer: 'Buyer', seller: 'Seller', system: 'cutthe6.com', admin: 'cutthe6.com' };
export const actorLabel = (actorType) => ACTOR_LABEL[actorType] || null;

/**
 * A timeline entry's label. Notice events use the notice's printed title;
 * flags use the copy-§9 short labels; the clock's events use copy §6.
 *
 * @param {object} event - A `ContingencyEventOut`.
 * @param {object} contract - The contingency's `contract` block.
 * @param {object} [notice] - The notice summary the event refers to.
 */
export function eventLabel(event, contract, notice) {
  const vars = {
    heading: contract?.heading,
    deadline_date: deadlineDate(contract),
    cure_deadline_date: cureDeadlineDate(contract),
  };
  const filled = (key) => {
    const text = contractCopy(key, vars);
    return hasPlaceholder(text) ? null : text;
  };
  switch (event?.event_type) {
    case 'notice_delivered':
      return noticeTitle(notice?.notice_type || event.details?.notice_type) || contractCopy('WI.timeline.notice_delivered');
    case 'treated_as_satisfied':
      return filled('WI.event.treated_as_satisfied') || contractCopy('WI.hub.status.treated_as_satisfied');
    case 'cure_lapsed':
      return filled('WI.event.cure_lapsed') || contractCopy('WI.hub.status.terminated_cure_lapsed');
    case 'land_division_lapsed':
      return filled('WI.event.land_division_lapsed') || contractCopy('WI.hub.status.land_division_lapsed');
    case 'admin_deal_record':
      // Copy §15.2: the deal-level record (contingency_id NULL) that carries the staff note and the document.
      return contractCopy('WI.timeline.deal_ended_by_agreement');
    case 'deal_terminated':
      return contractCopy(event.reason === REASON_ADMIN_AGREEMENT || event.details?.reason === REASON_ADMIN_AGREEMENT
        ? 'WI.timeline.deal_ended_by_agreement'
        : 'WI.timeline.deal_terminated');
    case 'moot':
      return contractCopy('WI.hub.status.moot');
    case 'attention_set':
      return contractCopy(`WI.attention.${event.reason}.short`) || contractCopy('WI.timeline.attention_set');
    case 'offplatform_notice_reported':
      return contractCopy('WI.attention.offplatform_notice.short');
    case 'cure_disputed':
      return contractCopy('WI.attention.cure_disputed.short');
    case 'land_division_disputed':
      return contractCopy('WI.attention.land_division_disputed.short');
    case 'not_applicable_disputed':
      return contractCopy('WI.attention.not_applicable_disputed.short');
    case 'seeded':
      return contractCopy('WI.timeline.seeded');
    case 'anchored':
      return contractCopy('WI.timeline.anchored');
    case 'admin_resolution':
      return contractCopy(event.details?.outcome === OUTCOME_RECORD_NOT_APPLICABLE
        ? 'WI.timeline.admin_resolution.record_not_applicable'
        : 'WI.timeline.admin_resolution');
    default:
      return String(event?.event_type || 'Event').replace(/_/g, ' ');
  }
}
